import { useEffect, useState } from "react";
import { apiGet } from "../api";
import ResultModal from "../components/ResultModal";

interface GenRecord {
  id: string; templateId: string; templateName: string;
  selfieUrl: string; resultUrl: string; qrPayload: string;
  status: string; errorMsg: string;
  createdAt: string; finishedAt: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  pending: "排队",
  processing: "生成中",
  done: "成功",
  failed: "失败",
};
const STATUS_STYLE: Record<string, string> = {
  pending: "bg-slate-100 text-slate-600",
  processing: "bg-sky-100 text-sky-700",
  done: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700",
};

export default function RecordsPage() {
  const [rows, setRows] = useState<GenRecord[]>([]);
  const [preview, setPreview] = useState<GenRecord | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    apiGet<GenRecord[]>("/records?limit=100").then(setRows).catch((e) => setErr(String(e)));
  }, []);

  const canReplay = (r: GenRecord) => r.status === "done" && !!r.resultUrl;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-slate-800">生成记录</h1>
        <p className="text-xs text-slate-500 mt-0.5">最近 100 条,按时间倒序。成功记录可重新展示结果和二维码。</p>
      </div>
      {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
      {rows.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-400">
          还没有生成记录
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 sm:gap-4 p-3">
              <div className="w-14 h-14 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                {r.selfieUrl && <img src={r.selfieUrl} alt="selfie" className="w-full h-full object-cover" />}
              </div>
              <div className="w-14 h-14 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                {r.resultUrl && <img src={r.resultUrl} alt="result" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800 truncate">{r.templateName || "(模板已删除)"}</div>
                <div className="text-xs text-slate-400">{new Date(r.createdAt).toLocaleString()}</div>
                {r.errorMsg && <div className="text-xs text-rose-500 mt-1 truncate">{r.errorMsg}</div>}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-shrink-0">
                <div className={`text-xs px-2 py-1 rounded text-center ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-600"}`}>
                  {STATUS_LABELS[r.status] || r.status}
                </div>
                <button
                  type="button"
                  onClick={() => setPreview(r)}
                  disabled={!canReplay(r)}
                  className="px-2.5 py-1.5 text-xs rounded border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={canReplay(r) ? "再次展示结果和二维码" : "只有成功且有结果图的记录可以展示"}
                >
                  展示结果/二维码
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {preview && (
        <ResultModal
          result={{
            id: preview.id,
            resultUrl: preview.resultUrl,
            qrPayload: preview.qrPayload || `${window.location.origin}/r/${preview.id}`,
            templateName: preview.templateName || "换脸大片",
          }}
          onReset={() => setPreview(null)}
          resetLabel="关闭"
          resetHint=""
        />
      )}
    </div>
  );
}
